import { useState } from "react";
import {
    Box,
    Typography,
    Button,
    IconButton,
    TextField,
    MenuItem,
    Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import themeColors from "../theme";

/**
 * First-time orientation tour for a state's CME Tracker page (CmeStatePage).
 *
 * Shown only once per user — gated by the parent via a single localStorage flag —
 * for whichever state they open first. It walks through four steps:
 *   0. License expiration date (spotlight on the expiration row + a date input)
 *   1. Progress ring (informational spotlight on the donut)
 *   2. Requirements breakdown (informational spotlight on the requirements list)
 *   3. Upload category (centered modal that seeds the first upload's category)
 *
 * Steps 0–2 render a "spotlight" cutout over the target element (a DOMRect the
 * parent recomputes on step change, scroll, and resize) plus a popup card.
 * Step 3 is a centered modal over a plain dim.
 */
const TOTAL_STEPS = 4;

export default function CmeTrackerTour({
    stepIndex,
    targetRect,
    stateName,
    categoryOptions = [],
    initialExpiration = "",
    onSaveExpiration,
    onNext,
    onSelectCategory,
    onClose,
}) {
    const { teal } = themeColors;
    const [expiration, setExpiration] = useState(initialExpiration || "");
    const [category, setCategory] = useState(categoryOptions[0] || "");

    // ---- Step 3: centered category modal -----------------------------------
    if (stepIndex === 3) {
        return (
            <>
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.55)",
                        zIndex: 1300,
                    }}
                />
                <Box
                    sx={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 440,
                        maxWidth: "calc(100vw - 32px)",
                        backgroundColor: "#fff",
                        borderRadius: "12px",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                        p: 4,
                        zIndex: 1400,
                    }}
                >
                    <IconButton
                        aria-label="Close tour"
                        onClick={onClose}
                        size="small"
                        sx={{ position: "absolute", top: 8, right: 8, color: "#9E9E9E" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Typography variant="body1" color="#2C3E50" sx={{ mb: 3, mt: 1 }}>
                        Please select a category below to upload your first CME certification
                    </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Certification Category:
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        sx={{ mb: 3 }}
                    >
                        {categoryOptions.map((c) => (
                            <MenuItem key={c} value={c}>
                                {c}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => onSelectCategory(category)}
                        sx={{
                            backgroundColor: teal,
                            textTransform: "none",
                            borderRadius: "8px",
                            py: 1.25,
                            fontWeight: 600,
                            "&:hover": { backgroundColor: "#005F5E" },
                        }}
                    >
                        Continue
                    </Button>

                    <Box sx={{ textAlign: "center", mt: 2 }}>
                        <Link
                            component="button"
                            onClick={onClose}
                            sx={{ color: "#6C757D", textDecoration: "none", fontWeight: 500 }}
                        >
                            Close
                        </Link>
                    </Box>
                </Box>
            </>
        );
    }

    // ---- Steps 0–2: spotlight + popup card ----------------------------------
    if (!targetRect) return null;

    const stepText = [
        `Please enter the expiration date of your license for "${stateName}"`,
        "This progress ring tracks your completed CME credits and updates automatically as you upload certificates.",
        "This section provides a detailed breakdown of your state's CME requirements, allowing you to track progress toward each individual requirement.",
    ][stepIndex];

    // Pad the spotlight slightly so the target breathes inside the cutout.
    const pad = 8;
    const rect = {
        top: targetRect.top - pad,
        left: targetRect.left - pad,
        width: targetRect.width + pad * 2,
        height: targetRect.height + pad * 2,
    };

    // Decide whether the card sits below or above the target based on free space.
    const margin = 16;
    const cardWidth = 380;
    const spaceBelow = window.innerHeight - targetRect.bottom;
    const placeBelow = spaceBelow > 260;

    const cardLeft = Math.min(
        Math.max(targetRect.left + targetRect.width / 2, cardWidth / 2 + 12),
        window.innerWidth - cardWidth / 2 - 12
    );
    const cardTop = placeBelow ? targetRect.bottom + margin : targetRect.top - margin;
    const cardTransform = placeBelow
        ? "translateX(-50%)"
        : "translate(-50%, -100%)";

    const handleNext = () => {
        if (stepIndex === 0 && onSaveExpiration) onSaveExpiration(expiration);
        onNext();
    };

    return (
        <>
            {/* Spotlight: dims everything except the active target via a huge
                box-shadow spread, leaving the real element visible underneath. */}
            <Box
                sx={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    borderRadius: "12px",
                    border: `2px solid ${teal}`,
                    backgroundColor: "rgba(0, 127, 126, 0.12)",
                    boxShadow: `0 0 0 9999px rgba(0,0,0,0.55), 0 0 24px rgba(0,127,126,0.6)`,
                    pointerEvents: "none",
                    zIndex: 1300,
                    transition: "all 0.25s ease",
                }}
            />

            {/* Popup card */}
            <Box
                sx={{
                    position: "fixed",
                    top: cardTop,
                    left: cardLeft,
                    transform: cardTransform,
                    width: cardWidth,
                    maxWidth: "calc(100vw - 32px)",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                    p: 3,
                    zIndex: 1400,
                    textAlign: "center",
                }}
            >
                <IconButton
                    aria-label="Close tour"
                    onClick={onClose}
                    size="small"
                    sx={{ position: "absolute", top: 6, right: 6, color: "#9E9E9E" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography variant="body1" color="#2C3E50" sx={{ mb: 2, mt: 1 }}>
                    {stepText}
                </Typography>

                {stepIndex === 0 && (
                    <TextField
                        type="date"
                        fullWidth
                        value={expiration}
                        onChange={(e) => setExpiration(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ mb: 2 }}
                    />
                )}

                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                    <Typography variant="caption" color="#9E9E9E">
                        {stepIndex + 1} of {TOTAL_STEPS}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                            backgroundColor: teal,
                            textTransform: "none",
                            borderRadius: "8px",
                            px: 3,
                            "&:hover": { backgroundColor: "#005F5E" },
                        }}
                    >
                        {stepIndex === 0 ? "Save & Continue" : "OK, Got It"}
                    </Button>
                </Box>
            </Box>
        </>
    );
}
